import { CNS_API_BASE } from "@/config";
import {
  parseConversationSuggestions,
  parseSuggestionActivation,
  type ConversationSuggestions,
  type SuggestionActivation,
} from "@/lib/suggestions/conversationSuggestionContract";
import { parseProblemDetail } from "@/types/problem-detail";

export type SuggestionRecoveryResult =
  | { status: "current"; suggestions: ConversationSuggestions; etag?: string }
  | { status: "not-modified"; etag?: string }
  | { status: "empty" };

export class SuggestionActivationError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "SuggestionActivationError";
  }
}

export async function fetchConversationSuggestions(
  projectId: string,
  conversationId: string,
  headMessageId: string,
  etag?: string,
): Promise<SuggestionRecoveryResult> {
  return requestConversationSuggestions(
    "GET",
    projectId,
    conversationId,
    headMessageId,
    etag,
  );
}

/**
 * Idempotently creates or refreshes the current actor's suggestions for a stable head.
 * The request has no body: actor and conversation access come from the authenticated session.
 */
export async function ensureConversationSuggestions(
  projectId: string,
  conversationId: string,
  headMessageId: string,
  etag?: string,
): Promise<SuggestionRecoveryResult> {
  return requestConversationSuggestions(
    "PUT",
    projectId,
    conversationId,
    headMessageId,
    etag,
  );
}

async function requestConversationSuggestions(
  method: "GET" | "PUT",
  projectId: string,
  conversationId: string,
  headMessageId: string,
  etag?: string,
): Promise<SuggestionRecoveryResult> {
  const query = new URLSearchParams({ headMessageId });
  const response = await fetch(
    `${CNS_API_BASE}/projects/${encodeURIComponent(projectId)}/conversations/${encodeURIComponent(conversationId)}/suggestions?${query}`,
    {
      method,
      credentials: "include",
      headers: etag ? { "If-None-Match": etag } : undefined,
    },
  );

  if (response.status === 304) {
    return { status: "not-modified", etag: response.headers.get("ETag") ?? etag };
  }
  if (response.status === 204) {
    return { status: "empty" };
  }
  if (!response.ok) {
    const problem = await parseProblemDetail(response);
    throw new Error(
      problem?.detail ??
        problem?.title ??
        (method === "PUT"
          ? "Failed to prepare suggestions"
          : "Failed to recover suggestions"),
    );
  }

  const suggestions = parseConversationSuggestions(await response.json());
  if (!suggestions) {
    throw new Error("Suggestion recovery returned an invalid contract");
  }
  return {
    status: "current",
    suggestions,
    etag: response.headers.get("ETag") ?? undefined,
  };
}

export async function activateConversationSuggestion(
  projectId: string,
  conversationId: string,
  suggestionId: string,
  request: { expectedHeadMessageId: string },
  etag: string,
): Promise<SuggestionActivation> {
  const response = await fetch(
    `${CNS_API_BASE}/projects/${encodeURIComponent(projectId)}/conversations/${encodeURIComponent(conversationId)}/suggestions/${encodeURIComponent(suggestionId)}/activation`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "If-Match": etag,
      },
      body: JSON.stringify(request),
    },
  );
  if (!response.ok) {
    const problem = await parseProblemDetail(response);
    const code =
      problem && typeof problem === "object" && "code" in problem
        ? String(problem.code)
        : undefined;
    throw new SuggestionActivationError(
      problem?.detail ?? problem?.title ?? "Failed to activate suggestion",
      response.status,
      code,
    );
  }
  const activation = parseSuggestionActivation(await response.json());
  if (!activation) {
    throw new SuggestionActivationError(
      "Suggestion activation returned an invalid contract",
      response.status,
    );
  }
  return activation;
}
